// src/pages/admin/tabs/MobileBookingsTab.jsx
// ─────────────────────────────────────────────────────────────────────────────
// Touch-optimized (app-like) list of WhatsApp video-call bookings for the mobile
// admin dashboard. Readable by any active admin; status/reschedule/reassign
// actions are restricted to super admins (the backend also enforces this).
// Talks to products-app /api/video-call/bookings* — same endpoints as the
// desktop BookingsTab, presented as swipe-friendly cards instead of a table.
// ─────────────────────────────────────────────────────────────────────────────

import { useState, useEffect } from "react";
import { useAuth } from "../../../hooks/useAuth";
import { B, fonts, Spinner, Btn } from "../../../components/UI";

const PRODUCTS_API_URL =
  process.env.REACT_APP_PRODUCTS_API_URL || "https://products.nilamsynthetics.com";
const API = (p) => `${PRODUCTS_API_URL}/api/video-call${p}`;
const NOTIF_API = (p) => `${PRODUCTS_API_URL}/api/notifications${p}`;

const STATUS_STYLE = {
  confirmed: { bg: B.greenLight, color: B.green, label: "Confirmed" },
  completed: { bg: B.orangeLight || "#f9eee8", color: B.textMid, label: "Completed" },
  cancelled: { bg: B.redLight, color: B.red, label: "Cancelled" },
  no_show: { bg: B.redLight, color: B.red, label: "No-show" },
};

const inputStyle = {
  width: "100%", padding: "12px 12px", fontSize: 14, fontWeight: 500,
  fontFamily: fonts.body, border: `1.5px solid ${B.border}`,
  borderRadius: 12, outline: "none", background: B.white, color: B.text,
  boxSizing: "border-box",
};

const displayDate = (iso) => {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  if (!y || !m || !d) return iso;
  return `${d}/${m}/${y}`;
};

const fmtPhone = (a) =>
  (a.whatsapp_phone || a.mobile_national || "—").replace(/^(91)/, "+91 ");

const actionBtnStyle = (color, disabled) => ({
  border: `1.5px solid ${color}55`, background: `${color}18`, color,
  borderRadius: 9, padding: "9px 12px", fontSize: 12, fontWeight: 700,
  fontFamily: fonts.body, cursor: disabled ? "not-allowed" : "pointer",
  opacity: disabled ? 0.45 : 1,
});

const dayOffsetISO = (offsetDays) => {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

export default function MobileBookingsTab({ showToast }) {
  const { user, isSuperAdmin } = useAuth();

  const [loading, setLoading] = useState(true);
  const [bookings, setBookings] = useState([]);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState(null);

  // Filters (default: From today, To tomorrow, Status confirmed).
  const [from, setFrom] = useState(dayOffsetISO(0));
  const [to, setTo] = useState(dayOffsetISO(1));
  const [status, setStatus] = useState("confirmed");
  const [phone, setPhone] = useState("");

  // Active representatives available for assignment.
  const [reps, setReps] = useState([]);

  // Optional inline cancellation-reason modal (mobile-friendly prompt replacement).
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelSaving, setCancelSaving] = useState(false);

  const flash = (msg) => {
    setNotice(msg);
    setTimeout(() => setNotice(""), 3500);
  };

  const getToken = async () => {
    if (!user) throw new Error("Not signed in.");
    return user.getIdToken();
  };

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const token = await getToken();
      const q = new URLSearchParams();
      if (from) q.set("from", from);
      if (to) q.set("to", to);
      if (status) q.set("status", status);
      if (phone.trim()) q.set("phone", phone.trim());
      const res = await fetch(`${API("/bookings")}?${q.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || "Failed to load bookings.");
      setBookings(json.data || []);
    } catch (e) {
      setError(e.message || "Failed to load bookings.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-line */ }, []);

  // Load the list of active representatives for the assign / test-send UI.
  const loadReps = async () => {
    try {
      const token = await getToken();
      const res = await fetch(API("/reps"), { headers: { Authorization: `Bearer ${token}` } });
      const json = await res.json().catch(() => ({}));
      if (res.ok) setReps(json.data || []);
    } catch { /* non-fatal — assignment falls back to the rep list */ }
  };
  useEffect(() => { loadReps(); /* eslint-disable-line */ }, []);

  const doAction = async (id, body, successMsg) => {
    if (!isSuperAdmin) return;
    setBusyId(id);
    setError("");
    try {
      const token = await getToken();
      const res = await fetch(API(`/bookings/${id}`), {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(json.error || (json.errors && json.errors.join("; ")) || "Action failed.");
        return;
      }
      flash(successMsg);
      if (showToast) showToast(successMsg);
      await load();
    } catch (e) {
      setError(e.message || "Action failed.");
    } finally {
      setBusyId(null);
    }
  };

  const handleReassign = async (a, repId) => {
    if (!isSuperAdmin) return;
    const rep = reps.find((r) => r.id === repId);
    const body = rep
      ? { assigned_rep_uid: rep.id, assigned_rep_name: rep.name, assigned_rep_phone: rep.whatsapp_phone }
      : { assigned_rep_uid: null, assigned_rep_name: null, assigned_rep_phone: null };
    await doAction(a.id, body, rep ? `Assigned to ${rep.name}` : "Rep unassigned.");
  };

  const handleSendReminder = async (a) => {
    if (!isSuperAdmin) return;
    setBusyId(a.id);
    setError("");
    try {
      const token = await getToken();
      const res = await fetch(NOTIF_API("/rep-reminders/send"), {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          rep_id: a.assigned_rep_uid || undefined,
          whatsapp_phone: a.assigned_rep_phone || "",
          name: a.assigned_rep_name || "",
          customer_name: a.customer_name,
          appointment_date: a.appointment_date,
          start_time: a.start_time,
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) { setError(json.error || "Send failed."); return; }
      flash(`Reminder sent to ${a.assigned_rep_name || a.assigned_rep_phone}.`);
      if (showToast) showToast(`Reminder sent to ${a.assigned_rep_name || a.assigned_rep_phone}.`);
    } catch (e) {
      setError(e.message || "Send failed.");
    } finally {
      setBusyId(null);
    }
  };

  const cancelTargetBooking = async () => {
    if (!cancelTarget) return;
    setCancelSaving(true);
    setError("");
    try {
      await doAction(
        cancelTarget.id,
        { status: "cancelled", ...(cancelReason.trim() ? { cancellation_reason: cancelReason.trim() } : {}) },
        "Booking cancelled."
      );
      setCancelTarget(null);
      setCancelReason("");
    } finally {
      setCancelSaving(false);
    }
  };

  return (
    <div style={{ padding: "14px 14px 120px" }}>
      {/* Header + count */}
      <div style={{ marginBottom: 14 }}>
        <p style={{ margin: 0, fontSize: 13, color: B.textLight, fontFamily: fonts.body }}>
          {loading ? "Refreshing bookings…" : `${bookings.length} booking${bookings.length === 1 ? "" : "s"}`}
        </p>
      </div>

      {notice && (
        <div style={{ background: B.greenLight, color: B.green, border: `1px solid ${B.green}`, borderRadius: 10, padding: "10px 14px", fontSize: 13, fontFamily: fonts.body, marginBottom: 12 }}>
          ✓ {notice}
        </div>
      )}
      {error && (
        <div style={{ background: B.redLight, color: B.red, border: `1px solid ${B.red}`, borderRadius: 10, padding: "10px 14px", fontSize: 13, fontFamily: fonts.body, marginBottom: 12 }}>
          {error}
        </div>
      )}

      {/* ─── Filters ─── */}
      <div style={{ background: B.white, border: `1px solid ${B.borderLight}`, borderRadius: 14, padding: "14px 14px" }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: B.textMid, marginBottom: 10, fontFamily: fonts.body }}>Filters</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: B.textMid, marginBottom: 4, display: "block", fontFamily: fonts.body }}>From</label>
            <input style={inputStyle} type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: B.textMid, marginBottom: 4, display: "block", fontFamily: fonts.body }}>To</label>
            <input style={inputStyle} type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
        </div>
        <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: B.textMid, marginBottom: 4, display: "block", fontFamily: fonts.body }}>Status</label>
            <select style={inputStyle} value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">All</option>
              <option value="confirmed">Confirmed</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
              <option value="no_show">No-show</option>
            </select>
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: B.textMid, marginBottom: 4, display: "block", fontFamily: fonts.body }}>Phone</label>
            <input style={inputStyle} value={phone} placeholder="Search" inputMode="tel" onChange={(e) => setPhone(e.target.value)} />
          </div>
        </div>
        <Btn variant="primary" style={{ width: "100%", marginTop: 12 }} onClick={load}>
          {loading ? "Loading…" : "Apply filters"}
        </Btn>
      </div>

      {/* ─── List ─── */}
      {loading && bookings.length === 0 && (
        <div style={{ marginTop: 18, textAlign: "center", color: B.textMid, fontFamily: fonts.body, fontSize: 13 }}>
          <Spinner /> <span style={{ marginLeft: 8 }}>Loading bookings…</span>
        </div>
      )}

      {!loading && bookings.length === 0 && (
        <p style={{ color: B.textLight, fontFamily: fonts.body, fontSize: 13, textAlign: "center", marginTop: 18 }}>
          No bookings match the current filters.
        </p>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 4 }}>
        {bookings.map((a) => {
          const st = STATUS_STYLE[a.status] || STATUS_STYLE.confirmed;
          return (
            <div key={a.id} style={{ background: B.white, border: `1px solid ${B.borderLight}`, borderRadius: 14, padding: "14px 14px" }}>
              {/* Header row: number + status */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <span style={{ fontSize: 15, fontWeight: 700, color: B.text, fontFamily: fonts.body }}>
                  {a.appointment_number || a.id.slice(0, 6)}
                </span>
                <span style={{ background: st.bg, color: st.color, borderRadius: 6, padding: "3px 10px", fontWeight: 700, fontSize: 11, fontFamily: fonts.body }}>
                  {st.label}
                </span>
              </div>

              {/* Date & time */}
              <div style={{ fontSize: 13, color: B.textMid, fontFamily: fonts.body, display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
                🗓 {displayDate(a.appointment_date)}
                <span style={{ color: B.textLight }}>·</span>
                <span style={{ color: B.text }}>{a.start_time}–{a.end_time}</span>
              </div>

              {/* Customer / phone / source */}
              <div style={{ marginTop: 6, display: "flex", flexDirection: "column", gap: 3 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: B.text, fontFamily: fonts.body }}>
                  {a.customer_name || "—"}{a.business_name ? <span style={{ color: B.textLight, fontSize: 12 }}> · {a.business_name}</span> : null}
                </div>
                <div style={{ fontSize: 13, color: B.textMid, fontFamily: fonts.body }}>📞 {fmtPhone(a)}</div>
                <div style={{ fontSize: 12, color: B.textLight, fontFamily: fonts.body }}>Source: {a.source || "—"}{a.assigned_rep_phone ? ` · Reminder: ${a.reminders_sent?.rep ? "sent" : "pending"}` : ""}</div>
              </div>

              {/* Representative */}
              <div style={{ marginTop: 10, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                <span style={{ fontSize: 13, color: B.textMid, fontFamily: fonts.body, whiteSpace: "nowrap" }}>👤 Rep</span>
                {isSuperAdmin && a.status === "confirmed" ? (
                  <select
                    disabled={busyId === a.id || cancelSaving}
                    value={a.assigned_rep_uid || ""}
                    onChange={(e) => handleReassign(a, e.target.value)}
                    title="Assign representative"
                    style={{ flex: 1, padding: "9px 10px", fontSize: 13, borderRadius: 10, border: `1.5px solid ${B.border}`, background: B.white, color: B.text, fontFamily: fonts.body, outline: "none", cursor: "pointer" }}
                  >
                    {a.assigned_rep_name && !reps.some((r) => r.id === a.assigned_rep_uid)
                      ? <option value="">{a.assigned_rep_name}</option>
                      : <option value="">Unassigned</option>}
                    {reps.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                  </select>
                ) : (
                  <span style={{ fontSize: 13, color: a.assigned_rep_name ? B.text : B.textLight, fontFamily: fonts.body, fontWeight: a.assigned_rep_name ? 600 : 400 }}>
                    {a.assigned_rep_name || "—"}
                  </span>
                )}
              </div>

              {/* Actions (super admin, confirmed only) */}
              {isSuperAdmin && a.status === "confirmed" && (
                <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
                  <button type="button" disabled={busyId === a.id} onClick={() => doAction(a.id, { status: "completed" }, "Marked completed.")}
                    style={actionBtnStyle("#1DA851", busyId === a.id)}>✓ Completed</button>
                  <button type="button" disabled={busyId === a.id} onClick={() => doAction(a.id, { status: "no_show" }, "Marked no-show.")}
                    style={actionBtnStyle("#C0392B", busyId === a.id)}>✕ No-show</button>
                  <button type="button" disabled={busyId === a.id} onClick={() => { setCancelTarget(a); setCancelReason(""); }}
                    style={actionBtnStyle("#E74C3C", busyId === a.id)}>⛔ Cancel</button>
                  {a.assigned_rep_phone && (
                    <button type="button" disabled={busyId === a.id} onClick={() => handleSendReminder(a)}
                      style={actionBtnStyle("#25D366", busyId === a.id)}>🔔 Reminder</button>
                  )}
                </div>
              )}
              {busyId === a.id && (
                <div style={{ marginTop: 8, color: B.textLight, fontSize: 11, fontFamily: fonts.body }}>Updating…</div>
              )}
            </div>
          );
        })}
      </div>

      {/* ─── Cancellation-reason modal ─── */}
      {cancelTarget && (
        <div role="dialog" aria-modal="true" aria-label="Cancel booking"
          style={{ position: "fixed", inset: 0, zIndex: 5000, background: "rgba(20,16,12,0.45)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div style={{ width: "100%", maxWidth: 420, background: B.white, border: `1px solid ${B.borderLight}`, borderRadius: 16, boxShadow: "0 24px 48px rgba(0,0,0,0.18)", padding: "20px 20px 16px" }}>
            <h3 style={{ margin: 0, fontSize: 19, color: B.text, fontFamily: fonts.adminDisplay }}>Cancel booking</h3>
            <p style={{ margin: "8px 0 0", fontSize: 14, color: B.textMid, lineHeight: 1.6, fontFamily: fonts.body }}>
              {cancelTarget.customer_name || "Booking"} on {displayDate(cancelTarget.appointment_date)} at {cancelTarget.start_time}.
            </p>
            <label style={{ fontSize: 13, fontWeight: 600, color: B.textMid, marginTop: 14, display: "block", fontFamily: fonts.body }}
              htmlFor="cancel-reason">Reason (optional)</label>
            <input id="cancel-reason" style={inputStyle} value={cancelReason} placeholder="e.g. customer requested"
              onChange={(e) => setCancelReason(e.target.value)} />
            <div style={{ marginTop: 14, display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <Btn variant="ghost" onClick={() => setCancelTarget(null)}>Keep booking</Btn>
              <Btn variant="primary" disabled={cancelSaving} onClick={cancelTargetBooking}>
                {cancelSaving ? "Cancelling…" : "Cancel booking"}
              </Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}